import type { ShorthandValue, ButtonProps } from '@archipro-design/aria';
import {
    ReadMore,
    pxToRem,
    pxArrayToRem,
    useStyles,
    useAppDisplayModeContext,
    useTheme,
} from '@archipro-design/aria';
import * as S from './Summary.style';
import { useTracker } from '@archipro-website/tracker';
import { useWebsiteTrackingProxy } from '~/modules/tracking/hook/useWebsiteTrackingProxy';
import { useLoaderData } from '@remix-run/react';
import type { loader } from '~/routes/_app.project.$urlSegment._index';

interface SummaryData {
    Content: string;
}

interface SummaryProps {
    data: SummaryData;
}

const Summary = ({ data: { Content } }: SummaryProps) => {
    const theme = useTheme();
    const { css } = useStyles();
    const { mobile } = useAppDisplayModeContext();
    const tracker = useTracker();
    const { project } = useLoaderData<typeof loader>();
    const clickProxy = useWebsiteTrackingProxy({
        professionalID: project.Professional.ID,
        professionalTitle: project.Professional.Title,
        itemType: 'Project',
    });
    return (
        <div
            className={`grid ${mobile ? 'grid-cols-1' : 'grid-cols-[924px_1fr]'} md:mb-24 px-4.5 md:px-0 py-4.5 md:py-0`}
        >
            <ReadMore
                content={
                    <span
                        className={css(S.Content)}
                        dangerouslySetInnerHTML={{
                            __html: Content,
                        }}
                        onClick={clickProxy}
                    />
                }
                maxLines={5}
                className={css(S.Summary)}
                variables={{
                    toggleFontSize: mobile ? pxToRem(14) : pxToRem(20),
                    linkColor:
                        theme.siteVariables.colorScheme.default!.foreground,
                    contentLineHeight: theme.siteVariables.lineHeightSmall,
                    contentFontSizeInPx: mobile ? 14 : 16,
                    elementGap: pxToRem(20),
                    showGradient: false,
                    toggleButtonSpacing: mobile
                        ? pxArrayToRem([10, 0, 0, 0])
                        : pxArrayToRem([30, 0, 0, 0]),
                }}
                readMoreText="Read more"
                readLessText="Read less"
                toggleButton={
                    {
                        onClick: (_, clamped) => {
                            if (clamped) {
                                // Track the click event on More button
                                tracker.log('ProjectDetailsExpandClick', {
                                    url: new URL(window.location.href),
                                    targetTracker: 'archiproTracker',
                                });
                            }
                        },
                    } satisfies ShorthandValue<ButtonProps>
                }
            />
            <div className={css(S.Spacer)}></div>
        </div>
    );
};

export default Summary;
